import {
  Billing_Amount_Read,
  Billing_Invoice_Read,
  Billing_Session_Read,
  Billing_Summary_Read,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

export class BillingAmount {
  constructor(
    private _amount: string,
    private _currency: string,
  ) {}

  public get amount() {
    return this._amount;
  }

  public get currency() {
    return this._currency;
  }

  public static parse(proto: Billing_Amount_Read) {
    return new BillingAmount(proto.amount, proto.currency);
  }
}

export class BillingInvoice {
  constructor(
    private _id: string,
    private _amount_due: string,
    private _amount_paid: string,
    private _currency: string,
    private _due_date: Dayjs | null,
    private _hosted_invoice_url: string | null,
    private _invoice_pdf: string | null,
    private _line_descriptions: string[],
    private _number: string | null,
    private _status: string,
  ) {}

  public get id() {
    return this._id;
  }

  public get amountDue() {
    return this._amount_due;
  }

  public get amountPaid() {
    return this._amount_paid;
  }

  public get currency() {
    return this._currency;
  }

  public get dueDate() {
    return this._due_date;
  }

  public get hostedInvoiceUrl() {
    return this._hosted_invoice_url;
  }

  public get invoicePdf() {
    return this._invoice_pdf;
  }

  public get lineDescriptions() {
    return this._line_descriptions;
  }

  public get number() {
    return this._number;
  }

  public get status() {
    return this._status;
  }

  public static parse(proto: Billing_Invoice_Read) {
    return new BillingInvoice(
      proto.id,
      proto.amount_due,
      proto.amount_paid,
      proto.currency,
      proto.due_date ? dayjs(proto.due_date) : null,
      proto.hosted_invoice_url ?? null,
      proto.invoice_pdf ?? null,
      proto.line_descriptions,
      proto.number ?? null,
      proto.status,
    );
  }
}

export class BillingSummary {
  constructor(
    private _configured: boolean,
    private _amounts_due: BillingAmount[],
    private _next_due_date: Dayjs | null,
    private _paid_ytd: BillingAmount[],
    private _paid_ytd_truncated: boolean,
    private _bank_account_on_file: boolean,
    private _ach_eligible: boolean,
    private _open_invoices: BillingInvoice[],
    private _open_invoices_truncated: boolean,
  ) {}

  public get configured() {
    return this._configured;
  }

  public get amountsDue() {
    return this._amounts_due;
  }

  public get nextDueDate() {
    return this._next_due_date;
  }

  public get paidYtd() {
    return this._paid_ytd;
  }

  public get paidYtdTruncated() {
    return this._paid_ytd_truncated;
  }

  public get bankAccountOnFile() {
    return this._bank_account_on_file;
  }

  public get achEligible() {
    return this._ach_eligible;
  }

  public get openInvoices() {
    return this._open_invoices;
  }

  public get openInvoicesTruncated() {
    return this._open_invoices_truncated;
  }

  // Billing_Summary_Read is nullable at the type level (null until the first summary
  // refresh runs); callers narrow before calling parse, so this method's own contract
  // only accepts the non-null case.
  public static parse(proto: NonNullable<Billing_Summary_Read>) {
    return new BillingSummary(
      proto.configured,
      proto.amounts_due.map(BillingAmount.parse),
      proto.next_due_date ? dayjs(proto.next_due_date) : null,
      proto.paid_ytd.map(BillingAmount.parse),
      proto.paid_ytd_truncated,
      proto.bank_account_on_file,
      proto.ach_eligible,
      proto.open_invoices.map(BillingInvoice.parse),
      proto.open_invoices_truncated,
    );
  }
}

export class BillingSession {
  constructor(private _url: string) {}

  public get url() {
    return this._url;
  }

  public static parse(proto: Billing_Session_Read) {
    return new BillingSession(proto.url);
  }
}
